import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { addDays, format, parseISO, startOfMonth } from "date-fns";
import { usePagination } from "hooks";
import orderBy from "lodash/orderBy";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setOnChange,
  setOpen,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import type { CustomFilterOption } from "components/custom-filters-list/custom-filters-list.component";
import {
  AMBIENT_SCRIBE_LIST_FILTERS,
  AMBIENT_SCRIBE_PAUSED_FEATURE
} from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { AMBIENT_SCRIBE_REFETCH } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import {
  DEFAULT_AMBIENT_SCRIBES_QUERY,
  useCreateAmbientScribeMutation,
  useDeleteAmbientScribeMutation,
  useGetAmbientScribesQuery,
  withScribeListColumns
} from "services/ambient-scribe";
import type {
  AmbientScribe,
  AmbientScribeFilters
} from "services/types/ambient-scribe.type";
import { getFullName } from "utils/common";
import { safeRefetch } from "utils/services.util";

import { getScribeListStatuses } from "../medical-scribe-constants";
import type { RecordingData } from "../medical-scribe-db/index-db-types.type";
import {
  deleteChunksById,
  deleteRecordingById,
  getAllRecordings,
  getChunkById,
  getDB,
  getRecordingById
} from "../medical-scribe-db/medical-scribe-db.util";
import { getAmbientScribeStatus, mergeChunks } from "../utils";
import type { MedicalScribe } from "./medical-scribes.type";
import useScribeCustomFilterOptions, {
  SCRIBES_DEFAULT_FILTERS,
  type ScribesCustomCustomFiltersValues
} from "./scribe-filters/use-scribe-custom-filter-options.hook";
import { formatSecondsToHHMMSS } from "./scribes-list/utils";

/**
 * `created_at` bounds as the API wants them: `after_moment` inclusive, `before_moment`
 * EXCLUSIVE, both `yyyy-MM-dd` and resolved in the practice's timezone server-side.
 */
type DateRange = {
  after_moment: string | null;
  before_moment: string | null;
};

const API_DATE_FORMAT = "yyyy-MM-dd";

/**
 * A picked "to" date means "through the end of that day", but `before_moment` is a `<` bound —
 * so the day after is what goes on the wire. Same convention as the billing filters bars.
 */
export function toApiDateRange(
  fromDate: Date | null,
  toDate: Date | null
): DateRange {
  return {
    after_moment: fromDate ? format(fromDate, API_DATE_FORMAT) : null,
    before_moment: toDate ? format(addDays(toDate, 1), API_DATE_FORMAT) : null
  };
}

const EMPTY_DATE_RANGE: DateRange = {
  after_moment: null,
  before_moment: null
};

/**
 * Month-to-date, matching what `DateRangeFilter`'s own `selectedRange="month"` computes
 * (`startOfMonth(now)` .. `now`).
 *
 * Seeded into state up front rather than waiting for the filter's mount `onChange`, so the very
 * first request already carries the bound — otherwise the screen fetches everything, paints, and
 * then refetches narrower.
 */
export function monthToDateRange(now: Date = new Date()): DateRange {
  return toApiDateRange(startOfMonth(now), now);
}

const MedicalScribeSectionContext = React.createContext<
  | ({
      localRecords: RecordingData[];
      scribes: MedicalScribe[];
      fetchMore: () => void;
      hasMoreData: boolean;
      isLoading: boolean;
      refetch: () => void;
      /** `DateRangeFilter`'s `onChange` — takes the picked Dates, not API strings. */
      updateDateRange: (fromDate: Date | null, toDate: Date | null) => void;
      /** The Dates the picker should render, derived back from the API bounds. */
      dateRangeValue: { fromDate: Date | null; toDate: Date | null };
      areListFiltersEnabled: boolean;
      uploadRecording: (row: MedicalScribe) => void;
      dialogDetails: MedicalScribe | undefined;
      setDialogDetails: React.Dispatch<
        React.SetStateAction<MedicalScribe | undefined>
      >;
      deleteRecording: (row: MedicalScribe) => void;
      fetchLocalRecords: () => Promise<void>;
      isBusy: boolean;
      uploadingLocalId: string | undefined;
      currentFilters: AmbientScribeFilters;
      customFilters: CustomFilterOption[];
      selectedCustomFilters: ScribesCustomCustomFiltersValues;
      updateCustomFilter: (field: string, value: any) => void;
      setSelectedCustomFilters: React.Dispatch<
        React.SetStateAction<ScribesCustomCustomFiltersValues>
      >;
    } & DateRange)
  | null
>(null);

type MedicalScribeSectionProviderProps = {
  children?: React.ReactNode;
  patientId?: string | number;
  chartNoteId?: number;
};

export function MedicalScribeSectionProvider({
  children,
  patientId,
  chartNoteId
}: MedicalScribeSectionProviderProps) {
  const flags = useFlags();
  const isPausedFeatureEnabled = flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false;
  const areListFiltersEnabled = flags[AMBIENT_SCRIBE_LIST_FILTERS] === true;
  // The patient chart tab and the appointment card mount this provider scoped to one record, and
  // those surfaces show a patient's whole scribe history — a month-to-date default would silently
  // hide last year's recordings there. The default belongs to the standalone Scribes screen only.
  const isScopedToRecord = Boolean(patientId || chartNoteId);
  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);

  const [localRecords, setLocalRecords] = useState<RecordingData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingLocalId, setUploadingLocalId] = useState<string | undefined>(
    undefined
  );

  const fetchLocalRecords = useCallback(() => {
    return getAllRecordings().then((records) => {
      const result = orderBy(records, "id", "desc");
      setLocalRecords(result);
    });
  }, []);

  // Initialize with patientId/chartNoteId if provided to avoid first-render unfiltered API call
  const [currentFilters, setCurrentFilters] = useState<AmbientScribeFilters>(
    () => ({
      ...(patientId ? { patient: patientId } : {}),
      ...(chartNoteId ? { chart_note: chartNoteId } : {})
    })
  );

  const [selectedCustomFilters, setSelectedCustomFilters] =
    useState<ScribesCustomCustomFiltersValues>(SCRIBES_DEFAULT_FILTERS);

  const updateCustomFilter = useCallback(
    (field: string, value: any) => {
      return setCurrentFilters((prevFilters) => ({
        ...prevFilters,
        [field]: value
      }));
    },
    [setCurrentFilters]
  );

  const [dateRange, setDateRange] = useState<DateRange>(() =>
    areListFiltersEnabled && !isScopedToRecord
      ? monthToDateRange()
      : EMPTY_DATE_RANGE
  );

  const updateDateRange = useCallback(
    (fromDate: Date | null, toDate: Date | null) => {
      const next = toApiDateRange(fromDate, toDate);
      setDateRange((previous) =>
        // `DateRangeFilter` fires `onChange` on mount with the range it derived from
        // `selectedRange`, which is the same month-to-date window already seeded above. Returning
        // the previous object keeps that a genuine no-op — a new object here would be a new
        // `filters` identity, and so a redundant refetch of page 0 on every mount.
        previous.after_moment === next.after_moment &&
        previous.before_moment === next.before_moment
          ? previous
          : next
      );
    },
    []
  );

  /**
   * What the picker renders. `before_moment` is exclusive, so step back a day to show it.
   *
   * `parseISO`, never `new Date(string)`: the native constructor reads a date-only string as UTC
   * midnight, which renders as the PREVIOUS day everywhere west of UTC — i.e. every US practice.
   * date-fns parses a date-only string in local time, which is what `format` wrote.
   */
  const dateRangeValue = useMemo(
    () => ({
      fromDate: dateRange.after_moment
        ? parseISO(dateRange.after_moment)
        : null,
      toDate: dateRange.before_moment
        ? addDays(parseISO(dateRange.before_moment), -1)
        : null
    }),
    [dateRange.after_moment, dateRange.before_moment]
  );

  const customFilters = useScribeCustomFilterOptions({
    updateCustomFilter,
    selectedCustomFilters,
    setSelectedCustomFilters,
    areListFiltersEnabled
  });

  // Single source of truth for the request params. This REPLACES `currentFilters` wholesale
  // rather than merging, so every filter the screen supports has to be derived here — a key that
  // is only ever written by `updateCustomFilter` would be wiped the next time any other filter
  // changed.
  useEffect(() => {
    setCurrentFilters({
      staff: selectedCustomFilters?.staff?.value,
      clinic: selectedCustomFilters?.clinic?.value,
      // Lock patient/chart_note to props when provided
      patient: patientId ?? selectedCustomFilters.patient?.value,
      ...(chartNoteId ? { chart_note: chartNoteId } : {}),
      ...(areListFiltersEnabled
        ? {
            appointment_type: selectedCustomFilters.appointment_type?.value,
            ambient_scribe_template:
              selectedCustomFilters.ambient_scribe_template?.value,
            // `false` is a real selection ("Not linked"); only `null`/absent clears the filter.
            has_chart_note:
              selectedCustomFilters.has_chart_note?.value ?? undefined,
            after_moment: dateRange.after_moment ?? undefined,
            before_moment: dateRange.before_moment ?? undefined
          }
        : {})
    });
  }, [
    selectedCustomFilters,
    patientId,
    chartNoteId,
    areListFiltersEnabled,
    dateRange.after_moment,
    dateRange.before_moment
  ]);

  const filters = useMemo(() => {
    return {
      ...currentFilters,
      status: getScribeListStatuses(isPausedFeatureEnabled),
      // Only ask for the new columns behind the flag: `appointment_type` / `chart_note_id` do not
      // exist on a backend that predates this change, and RestQL fails the WHOLE read with a 400
      // on an unknown field — a blank screen, not a missing column.
      ...(areListFiltersEnabled
        ? { query: withScribeListColumns(DEFAULT_AMBIENT_SCRIBES_QUERY) }
        : {})
    };
  }, [currentFilters, isPausedFeatureEnabled, areListFiltersEnabled]);

  const {
    data = [],
    fetchMore,
    hasMoreData,
    isLoading: isPaginationLoading,
    isFetching,
    isUninitialized: isRemoteUninitialized,
    refetch: refetchRemote,
    clearData
  } = usePagination<AmbientScribeFilters, AmbientScribe>({
    useQuery: useGetAmbientScribesQuery,
    limit: 25,
    params: filters,
    options: {
      skip: !currentClinic?.id,
      refetchOnMountOrArgChange: true
    }
  });

  const remoteData = data;

  const scribes = useMemo(() => {
    const result: MedicalScribe[] = [];

    const remoteRecordsMap = new Map<string, AmbientScribe>();
    const localRecordsMap = new Map<string, RecordingData>();

    // Filter local records by patientId/chartNoteId when scoped (Bug 1 fix + appointment scoping)
    const effectiveLocalRecords = localRecords.filter((r) => {
      if (patientId && String(r.patientId) !== String(patientId)) return false;
      if (chartNoteId && r.chartNoteId !== chartNoteId) return false;
      return true;
    });

    remoteData.forEach((scribe) => {
      const { created_at, duration, patient, staff } = scribe;
      const practiceId = String(currentClinic?.practice?.id ?? 0);

      const key = [
        new Date(created_at ?? "").toISOString(),
        practiceId,
        staff?.id,
        patient?.id,
        duration ?? "-"
      ].join("-");

      if (!remoteRecordsMap.has(key)) {
        remoteRecordsMap.set(key, scribe);
      }
    });

    effectiveLocalRecords
      .filter((record) => record.created_at)
      .forEach((record) => {
        const { created_at, patientId, userId, duration } = record;

        const practiceId = String(currentClinic?.practice?.id ?? 0);
        const formattedDuration = duration
          ? formatSecondsToHHMMSS(duration)
          : "";

        const key = [
          new Date(created_at ?? "").toISOString(),
          practiceId,
          userId,
          patientId,
          formattedDuration ?? "-"
        ].join("-");

        if (!localRecordsMap.has(key)) {
          localRecordsMap.set(key, record);
        }
      });

    const localSet = new Set(localRecordsMap.keys());
    const remoteSet = new Set(remoteRecordsMap.keys());

    //add intersected records
    const intersectedIds = [
      ...new Set([...localSet].filter((key) => remoteSet.has(key)))
    ];

    intersectedIds.forEach((id) => {
      const localRecord = localRecordsMap.get(id);
      const remoteRecord = remoteRecordsMap.get(id);

      if (localRecord && remoteRecord) {
        result.push({
          id: Number(remoteRecord?.id || 0),
          local_id: localRecord.id,
          uploadStarted: localRecord.uploadStarted,
          has_transcription: Boolean(remoteRecord.has_transcription),
          duration: remoteRecord.duration ?? "00:00:00",
          recording_url: remoteRecord.recording,
          uploaded_at: new Date(remoteRecord?.created_at ?? "").toISOString(),
          created_at: new Date(localRecord?.startTime ?? "").toISOString(),
          staffName: remoteRecord.staff
            ? `${getFullName({
                firstName: remoteRecord.staff.first_name,
                lastName: remoteRecord.staff.last_name,
                title: remoteRecord.staff.title,
                suffix: remoteRecord.staff.suffix
              })}`
            : currentUser?.fullName,
          appointmentTypeName: remoteRecord.appointment_type?.name ?? null,
          templateName: remoteRecord.ambient_scribe_template?.name ?? null,
          chartNoteId: remoteRecord.chart_note_id ?? null,
          patient: {
            id: Number(remoteRecord.patient?.id),
            fullName:
              remoteRecord.patient && typeof remoteRecord.patient === "object"
                ? getFullName({
                    firstName: remoteRecord.patient?.first_name,
                    lastName: remoteRecord.patient?.last_name,
                    middleName: remoteRecord.patient?.middle_name,
                    preferredName: remoteRecord.patient?.preferred_name,
                    title: remoteRecord.patient?.title,
                    suffix: remoteRecord.patient?.suffix
                  })
                : localRecord.patientFullName
          },
          status: remoteRecord.status
        });
      }
    });

    //add local only records
    const localOnlyKeys = new Set(
      [...localSet].filter((key) => !remoteSet.has(key))
    );

    localOnlyKeys.forEach((id) => {
      const localRecord = localRecordsMap.get(id);

      // Skip streaming records - they should only show when matched with remote records
      if (localRecord && !localRecord.isStreaming) {
        const createdAt = new Date(localRecord?.startTime ?? "").toISOString();
        result.push({
          local_id: localRecord.id,
          uploadStarted: localRecord.uploadStarted,
          has_transcription: false,
          duration: localRecord.duration
            ? formatSecondsToHHMMSS(Number(localRecord.duration))
            : "00:00",

          uploaded_at: createdAt,
          created_at: createdAt,
          staffName: currentUser?.fullName,
          patient: {
            id: localRecord.patientId,
            fullName: localRecord.patientFullName
          },
          status: getAmbientScribeStatus(localRecord)
        });
      }
    });

    //add remote only records
    const remoteOnlyKeys = new Set(
      [...remoteSet].filter((key) => !localSet.has(key))
    );

    remoteOnlyKeys.forEach((id) => {
      const remoteRecord = remoteRecordsMap.get(id);

      if (remoteRecord) {
        result.push({
          id: Number(remoteRecord?.id || 0),
          duration: remoteRecord.duration ?? "00:00",
          recording_url: remoteRecord.recording,
          has_transcription: Boolean(remoteRecord.has_transcription),
          uploaded_at: new Date(remoteRecord?.created_at ?? "").toISOString(),
          created_at: new Date(remoteRecord?.created_at ?? "").toISOString(),
          staffName: `${getFullName({
            firstName: remoteRecord.staff.first_name,
            lastName: remoteRecord.staff.last_name,
            title: remoteRecord.staff.title,
            suffix: remoteRecord.staff.suffix
          })}`,
          appointmentTypeName: remoteRecord.appointment_type?.name ?? null,
          templateName: remoteRecord.ambient_scribe_template?.name ?? null,
          chartNoteId: remoteRecord.chart_note_id ?? null,
          status: remoteRecord.status,
          patient: {
            id: Number(remoteRecord.patient?.id),
            fullName:
              remoteRecord.patient && typeof remoteRecord.patient === "object"
                ? getFullName({
                    firstName: remoteRecord.patient?.first_name,
                    lastName: remoteRecord.patient?.last_name,
                    middleName: remoteRecord.patient?.middle_name,
                    preferredName: remoteRecord.patient?.preferred_name,
                    title: remoteRecord.patient?.title,
                    suffix: remoteRecord.patient?.suffix
                  })
                : ""
          }
        });
      }
    });

    return orderBy(result, "created_at", "desc");
  }, [
    currentClinic?.practice?.id,
    currentUser?.fullName,
    remoteData,
    localRecords,
    patientId,
    chartNoteId
  ]);

  const refetch = useCallback(async () => {
    fetchLocalRecords().then(() => {
      clearData();
      // Runs after a delete's async chain, by which time the list query may be
      // skipped (no clinic) or unmounted; a bare refetch then throws RTK #38.
      safeRefetch(refetchRemote, isRemoteUninitialized);
    });
  }, [fetchLocalRecords, refetchRemote, isRemoteUninitialized, clearData]);

  const [dialogDetails, setDialogDetails] = useState<MedicalScribe | undefined>(
    undefined
  );

  const [deleteAmbientScribe, { isLoading: isDeleting }] =
    useDeleteAmbientScribeMutation();

  const [createAmbientScribe, { isLoading: isCreating }] =
    useCreateAmbientScribeMutation();

  const deleteRecordingConfirmed = useCallback(
    (row: MedicalScribe) => {
      if (!row.id) {
        return deleteRecordingById(String(row.local_id)).then(() => {
          refetch();
        });
      } else {
        return deleteAmbientScribe(Number(row.id)).then(() => {
          deleteRecordingById(String(row.local_id)).then(() => {
            refetch();
          });
        });
      }
    },
    [deleteAmbientScribe, refetch]
  );

  const deleteRecording = useCallback(
    (row: MedicalScribe) => {
      dispatch(setTitle("Are you sure you want to delete audio recording?"));
      dispatch(
        setOnChange(() => {
          deleteRecordingConfirmed(row);
        })
      );
      dispatch(setOpen(true));
    },
    [deleteRecordingConfirmed, dispatch]
  );

  const uploadRecording = useCallback(
    async (row: MedicalScribe) => {
      const localId = String(row?.local_id ?? "");
      if (!localId) return;

      setIsUploading(true);
      setUploadingLocalId(localId);

      let markedUploadingRecord: RecordingData | null = null;
      try {
        const chunkResult = await getChunkById(localId);
        if (!chunkResult?.id) {
          throw new Error("No local audio chunks found for upload.");
        }

        const blob = mergeChunks(chunkResult.chunks);
        if (!blob || blob.size === 0) {
          dispatch(
            setCustomMessage(
              "Error: Audio recording is empty or invalid. Please try recording again."
            )
          );
          dispatch(setSnackbarOpen(true));
          return;
        }

        if (blob.size < 100) {
          console.warn(
            `Audio blob is very small (${blob.size} bytes), may be incomplete`
          );
        }

        const recording = await getRecordingById(localId);
        if (
          !recording ||
          recording.uploadStatus === "uploading" ||
          recording.uploadStarted
        ) {
          return;
        }

        const now = Date.now();
        markedUploadingRecord = {
          ...recording,
          uploadStatus: "uploading",
          uploadStarted: now
        };

        const db = await getDB();
        const markTx = db.transaction("recordings", "readwrite");
        await markTx.objectStore("recordings").put(markedUploadingRecord);
        await markTx.done;

        const audio_file = new File([blob], uuidv4(), { type: blob.type });
        const response = await createAmbientScribe({
          patient: Number(row.patient.id),
          duration: row.duration,
          practice: Number(currentClinic?.practice?.id),
          staff: Number(currentUser?.id),
          audio_file,
          clinic: currentClinic?.id
        });

        if ("error" in response || !response.data?.id) {
          if (markedUploadingRecord) {
            const failTx = db.transaction("recordings", "readwrite");
            await failTx.objectStore("recordings").put({
              ...markedUploadingRecord,
              uploadStatus: "failed",
              uploadStarted: 0
            });
            await failTx.done;
          }
          dispatch(
            setCustomMessage(
              "An Error has Occurred, please reach out to support."
            )
          );
          dispatch(setSnackbarOpen(true));
          return;
        }

        const remoteId = Number(response.data.id);
        const remoteCreatedAt = response.data.created_at;

        const successTx = db.transaction("recordings", "readwrite");
        await successTx.objectStore("recordings").put({
          ...markedUploadingRecord,
          uploadStatus: "uploaded",
          uploadStarted: 0,
          uploaded: now,
          medical_transcribe_id: remoteId,
          created_at: remoteCreatedAt
        });
        await successTx.done;

        // Audio chunks are no longer needed after successful upload.
        await deleteChunksById(localId);
        await refetch();

        dispatch(setCustomMessage("Transcription completed."));
        dispatch(setSnackbarOpen(3000));
      } catch (error) {
        if (markedUploadingRecord) {
          try {
            const db = await getDB();
            const failTx = db.transaction("recordings", "readwrite");
            await failTx.objectStore("recordings").put({
              ...markedUploadingRecord,
              uploadStatus: "failed",
              uploadStarted: 0
            });
            await failTx.done;
          } catch (markFailedError) {
            console.error(
              "Failed to mark recording upload as failed",
              markFailedError
            );
            // Keep original upload error as primary signal.
          }
        }
        dispatch(
          setCustomMessage(
            "An Error has Occurred, please reach out to support."
          )
        );
        dispatch(setSnackbarOpen(true));
      } finally {
        setIsUploading(false);
        setUploadingLocalId(undefined);
      }
    },
    [
      createAmbientScribe,
      currentClinic?.practice?.id,
      currentClinic?.id,
      currentUser?.id,
      dispatch,
      refetch
    ]
  );

  useEventBus(AMBIENT_SCRIBE_REFETCH, () => {
    refetch();
  });

  const value = useMemo(
    () => ({
      localRecords,
      scribes,
      fetchMore,
      hasMoreData,
      isLoading: isFetching || isPaginationLoading,
      refetch,
      after_moment: dateRange.after_moment,
      before_moment: dateRange.before_moment,
      updateDateRange,
      dateRangeValue,
      areListFiltersEnabled,
      uploadRecording,
      dialogDetails,
      setDialogDetails,
      deleteRecording,
      fetchLocalRecords,
      isBusy: isDeleting || isCreating || isUploading,
      uploadingLocalId,
      currentFilters,
      setCurrentFilters,
      selectedCustomFilters,
      customFilters,
      updateCustomFilter,
      setSelectedCustomFilters
    }),
    [
      localRecords,
      scribes,
      fetchMore,
      hasMoreData,
      isFetching,
      isPaginationLoading,
      refetch,
      dateRange.after_moment,
      dateRange.before_moment,
      updateDateRange,
      dateRangeValue,
      areListFiltersEnabled,
      uploadRecording,
      dialogDetails,
      deleteRecording,
      fetchLocalRecords,
      isDeleting,
      isCreating,
      isUploading,
      uploadingLocalId,
      currentFilters,
      selectedCustomFilters,
      customFilters,
      updateCustomFilter,
      setSelectedCustomFilters
    ]
  );

  return (
    <MedicalScribeSectionContext.Provider value={value}>
      {children}
    </MedicalScribeSectionContext.Provider>
  );
}

export default function useMedicalScribeSection() {
  const context = React.useContext(MedicalScribeSectionContext);
  if (!context) {
    throw new Error(
      `useMedicalScribeSection must be used within a MedicalScribeSectionProvider`
    );
  }

  return context;
}
